import { create } from 'zustand';
import axios from 'axios';

const TOKEN_KEY = 'clepto_token';
const USER_KEY = 'clepto_user';

function loadFromStorage() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

const initial = loadFromStorage();

const OAUTH_CONFIGS = {
  facebook: {
    authorizeUrl: 'https://www.facebook.com/v19.0/dialog/oauth',
    scope: 'public_profile,email',
    clientId: () => process.env.REACT_APP_FACEBOOK_APP_ID,
  },
  twitter: {
    authorizeUrl: 'https://twitter.com/i/oauth2/authorize',
    scope: 'tweet.read users.read',
    clientId: () => process.env.REACT_APP_TWITTER_CLIENT_ID,
  },
  tiktok: {
    authorizeUrl: 'https://www.tiktok.com/v2/auth/authorize/',
    scope: 'user.info.basic',
    clientId: () => process.env.REACT_APP_TIKTOK_CLIENT_KEY,
  },
};

function generateCodeVerifier() {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return btoa(String.fromCharCode(...arr))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

const useAuthStore = create((set, get) => ({
  token: initial.token,
  user: initial.user,
  savedGames: [],
  loading: false,
  error: null,

  setAuth: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    set({ token, user, error: null });
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    set({ token: null, user: null, savedGames: [], error: null });
  },

  register: async (email, phone, password, displayName) => {
    set({ loading: true, error: null });
    try {
      const res = await axios.post('/auth/register', { email, phone, password, displayName });
      if (res.data?.success) {
        const { token, user } = res.data.data;
        get().setAuth(token, user);
        set({ loading: false, savedGames: [] });
        return true;
      }
      set({ loading: false, error: res.data?.error || 'Erro ao registrar' });
      return false;
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao conectar com o servidor';
      set({ loading: false, error: msg });
      return false;
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const res = await axios.post('/auth/login', { email, password });
      if (res.data?.success) {
        const { token, user, savedGames } = res.data.data;
        get().setAuth(token, user);
        set({ loading: false, savedGames: savedGames || [] });
        return true;
      }
      set({ loading: false, error: res.data?.error || 'Erro ao fazer login' });
      return false;
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao conectar com o servidor';
      set({ loading: false, error: msg });
      return false;
    }
  },

  forgotPassword: async (email) => {
    set({ loading: true, error: null });
    try {
      const res = await axios.post('/auth/forgot-password', { email });
      set({ loading: false });
      return !!res.data?.success;
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao conectar com o servidor';
      set({ loading: false, error: msg });
      return false;
    }
  },

  resetPassword: async (token, password) => {
    set({ loading: true, error: null });
    try {
      const res = await axios.post('/auth/reset-password', { token, password });
      if (res.data?.success) {
        set({ loading: false });
        return true;
      }
      set({ loading: false, error: res.data?.error || 'Erro ao redefinir senha' });
      return false;
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao conectar com o servidor';
      set({ loading: false, error: msg });
      return false;
    }
  },

  socialLogin: async (provider, code, redirectUri, codeVerifier) => {
    set({ loading: true, error: null });
    try {
      const res = await axios.post('/auth/social', {
        provider, code, redirectUri, codeVerifier,
      });
      if (res.data?.success) {
        const { token, user, savedGames } = res.data.data;
        get().setAuth(token, user);
        set({ loading: false, savedGames: savedGames || [] });
        return true;
      }
      set({ loading: false, error: res.data?.error || 'Erro ao autenticar' });
      return false;
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao conectar com o servidor';
      set({ loading: false, error: msg });
      return false;
    }
  },

  openOAuthPopup: async (provider) => {
    const config = OAUTH_CONFIGS[provider];
    if (!config) return;

    const redirectUri = `${window.location.origin}/auth/callback`;
    const state = `${provider}:${Math.random().toString(36).slice(2)}`;

    let url;
    if (provider === 'twitter') {
      const verifier = generateCodeVerifier();
      const challenge = await generateCodeChallenge(verifier);
      sessionStorage.setItem('oauth_code_verifier', verifier);
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: config.clientId(),
        redirect_uri: redirectUri,
        scope: config.scope,
        state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
      });
      url = `${config.authorizeUrl}?${params}`;
    } else if (provider === 'tiktok') {
      const params = new URLSearchParams({
        client_key: config.clientId(),
        response_type: 'code',
        scope: config.scope,
        redirect_uri: redirectUri,
        state,
      });
      url = `${config.authorizeUrl}?${params}`;
    } else {
      const params = new URLSearchParams({
        client_id: config.clientId(),
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: config.scope,
        state,
      });
      url = `${config.authorizeUrl}?${params}`;
    }

    const width = 500, height = 600;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;
    const popup = window.open(url, 'oauth', `width=${width},height=${height},left=${left},top=${top}`);

    return new Promise((resolve) => {
      const handler = async (event) => {
        if (event.origin !== window.location.origin) return;
        if (!event.data?.type || event.data.type !== 'oauth_callback') return;

        window.removeEventListener('message', handler);
        const { code: authCode, provider: cbProvider } = event.data;

        if (authCode) {
          const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
          sessionStorage.removeItem('oauth_code_verifier');
          const ok = await get().socialLogin(cbProvider || provider, authCode, redirectUri, codeVerifier);
          resolve(ok);
        } else {
          resolve(false);
        }
      };
      window.addEventListener('message', handler);

      const pollTimer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', handler);
          resolve(false);
        }
      }, 500);
    });
  },

  validateToken: async () => {
    const { token } = get();
    if (!token) return false;
    try {
      const res = await axios.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        const { user, savedGames } = res.data.data;
        get().setAuth(token, user);
        set({ savedGames: savedGames || [] });
        return true;
      }
      get().logout();
      return false;
    } catch {
      get().logout();
      return false;
    }
  },

  getAuthHeader: () => {
    const { token } = get();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  saveGame: async (gameState, avatarData, vereadorData, cityInfo) => {
    const { token } = get();
    if (!token) return false;
    try {
      const res = await axios.post('/game/save', {
        slot: 1,
        cityName: cityInfo?.cityName,
        codigoIbge: cityInfo?.codigoIbge,
        stateSigla: cityInfo?.stateSigla,
        currentOffice: gameState?.currentOffice,
        turn: gameState?.turn,
        round: gameState?.round,
        gameState,
        avatarData,
        vereadorData,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.success || false;
    } catch (err) {
      console.error('Save game error:', err);
      return false;
    }
  },

  loadGame: async (slot = 1) => {
    const { token } = get();
    if (!token) return null;
    try {
      const res = await axios.get(`/game/load/${slot}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) return res.data.data;
      return null;
    } catch {
      return null;
    }
  },

  refreshSaves: async () => {
    const { token } = get();
    if (!token) return;
    try {
      const res = await axios.get('/game/saves', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        set({ savedGames: res.data.data || [] });
      }
    } catch {}
  },
}));

export default useAuthStore;
